<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (entity(); as e) {
        <span class="page-head__crumb page-head__crumb--current" aria-current="page">{{
          e.name
        }}</span>
      } @else {
        <span class="page-head__crumb page-head__crumb--current" aria-current="page">Business</span>
      }
    </nav>

    @if (entity(); as e) {
      <div class="page-head__bar">
        <div class="page-head__titles page-head__titles--icon">
          <span class="page-head__icon" aria-hidden="true"
            ><app-icon name="building" [size]="24"
          /></span>
          <h1 class="page-head__title" data-testid="entity-dashboard-title">{{ e.name }}</h1>
          @if (e.trading_name && e.trading_name !== e.name) {
            <p class="page-head__subtitle">Trading as {{ e.trading_name }}</p>
          }
        </div>
        <div class="page-head__actions">
          <span
            class="role-badge"
            [class.role-badge--admin]="e.your_role === 'entity-admin'"
            [class.role-badge--member]="e.your_role === 'entity-member'"
            [class.role-badge--agent]="e.your_role === 'agent'"
            [matTooltip]="getRoleTooltip(e.your_role)"
            >{{ getRoleLabel(e.your_role) }}</span
          >

          <!-- Actions menu (hamburger, top-right) — mirrors the agent dashboard. -->
          <div class="entity-menu">
            <button
              type="button"
              class="entity-menu__trigger"
              [class.entity-menu__trigger--open]="menuOpen()"
              (click)="toggleMenu()"
              aria-haspopup="menu"
              [attr.aria-expanded]="menuOpen()"
              aria-label="Open actions menu"
              data-testid="entity-actions-menu"
            >
              <app-icon name="menu" [size]="20" />
            </button>
            @if (menuOpen()) {
              <!-- Click-catcher closes the menu on outside click -->
              <button
                type="button"
                class="entity-menu__backdrop"
                aria-label="Close menu"
                (click)="closeMenu()"
              ></button>
              <div class="entity-menu__panel" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  (click)="goTo(['/entity', e.short_id, 'team'])"
                  data-testid="menu-team-btn"
                >
                  <app-icon name="users" [size]="18" />
                  <span>Team</span>
                </button>
                @if (canInvite(e)) {
                  <button
                    type="button"
                    role="menuitem"
                    (click)="openInviteDialog(e)"
                    data-testid="invite-to-company-btn"
                  >
                    <app-icon name="plus" [size]="18" />
                    <span>Invite</span>
                  </button>
                }
                @if (canApply(e) && !hasApplied()) {
                  <button
                    type="button"
                    role="menuitem"
                    (click)="
                      goTo(['/entity', e.short_id, 'applications', 'new', 'bank'], startNewParams)
                    "
                    data-testid="header-recommendation-btn"
                  >
                    <app-icon name="sparkles" [size]="18" />
                    <span>Get a recommendation</span>
                  </button>
                }
                @if (canDelete(e)) {
                  <button
                    type="button"
                    role="menuitem"
                    class="entity-menu__item--danger"
                    [disabled]="isDeleting()"
                    (click)="deleteEntity(e)"
                    data-testid="delete-business-btn"
                  >
                    <app-icon name="trash" [size]="18" />
                    <span>Delete business</span>
                  </button>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
  </header>

  @if (nameToConfirm(); as name) {
    <app-company-name-check [name]="name" (answered)="answerNameCheck($event)" />
  }
  @if (nameAnsweredNo()) {
    <p role="status" data-testid="company-name-no-note">
      Thanks. Please contact us at
      <a href="mailto:support@flow.capx.co.za">support&#64;flow.capx.co.za</a> to correct the
      registration number.
    </p>
  }

  @if (isLoading()) {
    <div class="loading-state" aria-label="Loading business details" aria-busy="true">
      <div class="skeleton-header">
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w40"></span>
        <span class="sg-skeleton sg-skeleton--text" style="width: 25%"></span>
      </div>
      <div class="skeleton-stats">
        @for (i of [1, 2, 3, 4, 5]; track i) {
          <span
            class="sg-skeleton"
            style="height: 80px; width: 100%; border-radius: var(--radius-md)"
          ></span>
        }
      </div>
      <div class="skeleton-rows">
        @for (i of [1, 2, 3]; track i) {
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w100"></span>
        }
      </div>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load business details</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" />
        Try again
      </button>
    </div>
  } @else if (entity(); as e) {
    @if (isAgent(e) && justOnboarded()) {
      <div class="agent-success" role="status">
        <span class="agent-success__icon"><app-icon name="check-circle" [size]="20" /></span>
        <div class="agent-success__body">
          <p class="agent-success__title">{{ e.name }} is set up</p>
          <p class="agent-success__text">
            We've emailed the responsible person an invite to set their password. You can start
            preparing their application now.
          </p>
        </div>
        <button
          type="button"
          class="agent-success__close"
          aria-label="Dismiss"
          (click)="dismissOnboarded()"
        >
          <app-icon name="x" [size]="18" />
        </button>
      </div>
    }

    @if (isAgent(e)) {
      <div class="agent-acting" role="note">
        <span class="agent-acting__icon"><app-icon name="shield" [size]="18" /></span>
        <p class="agent-acting__text">
          You're acting as agent for <strong>{{ e.name }}</strong
          >. You can prepare, submit and manage applications. Giving director consent stays with the
          business's directors.
        </p>
      </div>
    }

    @if (preAppNotice(); as notice) {
      <div
        class="preapp-notice"
        [class.preapp-notice--attention]="notice.status !== 'SUBMITTED' && notice.status !== 'OPEN'"
        role="status"
        data-testid="dashboard-preapp-notice"
      >
        <div class="preapp-notice__head">
          <app-status-chip [status]="notice.status" />
        </div>
        <div class="preapp-notice__body">
          <p class="preapp-notice__title">{{ notice.title }}</p>
          <p class="preapp-notice__text">{{ notice.body }}</p>
        </div>
        <div class="preapp-notice__actions">
          <a mat-stroked-button [routerLink]="notice.routerLink" data-testid="dashboard-preapp-cta">
            {{ notice.cta }}
          </a>
          @if (notice.secondaryLink) {
            <a
              mat-button
              [routerLink]="notice.secondaryLink"
              data-testid="dashboard-preapp-add-documents"
            >
              {{ notice.secondaryCta }}
            </a>
          }
        </div>
      </div>
    }

    <ul class="biz-facts" aria-label="About this business">
      <li class="biz-fact">
        <span class="biz-fact__icon"><app-icon name="tag" [size]="16" /></span>
        <span class="biz-fact__label">Type</span>
        <span class="biz-fact__value">{{ getTypeLabel(e.type) }}</span>
      </li>
      <li class="biz-fact">
        <span class="biz-fact__icon"><app-icon name="briefcase" [size]="16" /></span>
        <span class="biz-fact__label">Industry</span>
        <span class="biz-fact__value">{{ e.industry }}</span>
      </li>
      <li class="biz-fact">
        <span class="biz-fact__icon"><app-icon name="file-text" [size]="16" /></span>
        <span class="biz-fact__label">Registration number</span>
        <span class="biz-fact__value biz-fact__value--num">{{ e.reg_no }}</span>
      </li>
      <li class="biz-fact">
        <span class="biz-fact__icon"><app-icon name="calendar" [size]="16" /></span>
        <span class="biz-fact__label">Trading since</span>
        <span class="biz-fact__value">{{
          getTradingStartLabel(e.trading_start_month, e.trading_start_year)
        }}</span>
      </li>
    </ul>

    <!-- First-run: no applications yet + can apply → lead with the guided intake.
         The stat grid (all-zero for a new business) is suppressed below so the
         recommendation dominates; the two products stay available as secondary
         links inside the hero. -->
    @if (!hasApplied() && canApply(e)) {
      <mat-card
        appearance="outlined"
        class="first-run"
        [class.first-run--hero]="!preAppNotice()"
        [class.first-run--compact]="preAppNotice()"
        data-testid="first-run-hero"
      >
        <mat-card-content>
          @if (!preAppNotice()) {
            <span
              class="first-run__art"
              style="--art-src: url(/assets/illustrations/applications.svg)"
              aria-hidden="true"
            ></span>
          }
          <div class="first-run__header">
            <span class="first-run__icon" aria-hidden="true">
              <app-icon name="search" [size]="28" />
            </span>
            <div class="first-run__heading">
              <h2 class="first-run__title">Let's find the right funding for {{ e.name }}</h2>
              <p class="first-run__body">
                Not sure whether Kickstart Cash, Debtors Finance, or both fit your business? Answer
                one question and share a few documents — we'll point you to the right product. Takes
                about 2 minutes.
              </p>
            </div>
          </div>
          <a
            mat-flat-button
            color="primary"
            [routerLink]="['/entity', e.short_id, 'applications', 'new', 'bank']"
            [queryParams]="startNewParams"
            class="first-run__cta"
            data-testid="first-run-recommendation-btn"
          >
            <app-icon name="sparkles" [size]="18" />
            <span>Apply for funding</span>
          </a>

          <!-- Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. -->
          @if (!directApplicationsPaused) {
            <div class="first-run__direct">
              <span class="first-run__direct-label">Already know what you need?</span>
              <div class="first-run__direct-links">
                <a
                  class="first-run__product-link"
                  [routerLink]="['/entity', e.short_id, 'kickstart', 'new']"
                  data-testid="first-run-ks-link"
                >
                  <app-icon name="briefcase" [size]="16" />
                  <span>Apply for Kickstart Cash</span>
                  <app-icon name="arrow-right" [size]="16" />
                </a>
                <a
                  class="first-run__product-link"
                  [routerLink]="['/entity', e.short_id, 'df', 'new']"
                  data-testid="first-run-df-link"
                >
                  <app-icon name="trending-up" [size]="16" />
                  <span>Apply for Debtors Finance</span>
                  <app-icon name="arrow-right" [size]="16" />
                </a>
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- Returning applicant: one clear "New application" action, with the two
         products as direct-specify shortcuts beneath it — the same anatomy as the
         first-run hero, so the two states stay consistent. -->
    @if (hasApplied() && canApply(e)) {
      <mat-card
        appearance="outlined"
        class="first-run first-run--compact"
        data-testid="new-application-panel"
      >
        <mat-card-content>
          <h2 class="first-run__title">Start a new application</h2>
          <p class="first-run__body">
            Answer a couple of questions and we'll point you to the right product — Kickstart Cash,
            Debtors Finance, or both.
          </p>
          <a
            mat-flat-button
            color="primary"
            [routerLink]="['/entity', e.short_id, 'applications', 'new', 'bank']"
            [queryParams]="startNewParams"
            class="first-run__cta"
            data-testid="dashboard-new-application-btn"
          >
            <app-icon name="sparkles" [size]="18" />
            <span>New application</span>
          </a>

          <!-- Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. -->
          @if (!directApplicationsPaused) {
            <div class="first-run__direct">
              <span class="first-run__direct-label">Already know what you need?</span>
              <div class="first-run__direct-links">
                <a
                  class="first-run__product-link"
                  [routerLink]="['/entity', e.short_id, 'kickstart', 'new']"
                  data-testid="new-application-ks-link"
                >
                  <app-icon name="briefcase" [size]="16" />
                  <span>Apply for Kickstart Cash</span>
                  <app-icon name="arrow-right" [size]="16" />
                </a>
                <a
                  class="first-run__product-link"
                  [routerLink]="['/entity', e.short_id, 'df', 'new']"
                  data-testid="new-application-df-link"
                >
                  <app-icon name="trending-up" [size]="16" />
                  <span>Apply for Debtors Finance</span>
                  <app-icon name="arrow-right" [size]="16" />
                </a>
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- Portfolio metrics — a slim row below the action (or the only content for a
         view-only member who can't apply). -->
    @if (hasApplied() || !canApply(e)) {
      <div class="mini-stats">
        <div class="mini-stat" data-testid="applications-card">
          <span class="mini-stat__label">Applications</span>
          <span class="mini-stat__value">{{ activity().length }}</span>
        </div>
        <div class="mini-stat">
          <span class="mini-stat__label">Active funding</span>
          <span
            class="mini-stat__value"
            [class.mini-stat__value--empty]="activeFunding() === null"
            >{{ activeFunding() | dash }}</span
          >
        </div>
        <div class="mini-stat">
          <span class="mini-stat__label">Total funded</span>
          <span class="mini-stat__value" [class.mini-stat__value--empty]="totalFunded() === null">{{
            totalFunded() | dash
          }}</span>
        </div>
      </div>
    }

    <mat-card appearance="outlined" class="activity-card">
      <mat-card-content>
        <h2 class="activity-card__heading">Recent applications</h2>
        @if (recentActivity().length === 0) {
          <!-- First-time applicants are handled by the hero above; here we only
               show the plain empty message (e.g. for a view-only member). -->
          <div class="sg-empty">
            <span
              class="sg-empty__art"
              style="--art-src: url(/assets/illustrations/applications.svg)"
              aria-hidden="true"
            ></span>
            <p class="sg-empty__title">No applications yet</p>
            <p class="sg-empty__desc">Applications for this business will appear here.</p>
          </div>
        } @else {
          <ul class="activity-list">
            @for (item of recentActivity(); track item.short_id) {
              <li
                class="activity-list__item"
                [class.activity-list__item--draft]="item.isDraft"
                [attr.data-testid]="'activity-item-' + item.short_id"
                [attr.data-product]="item.productLabel"
              >
                <a class="activity-list__link" [routerLink]="item.routerLink">
                  <span class="activity-list__primary">
                    {{ item.productLabel }}
                    @if (item.amount !== null) {
                      · Applied for {{ item.amount | zar }}
                    }
                  </span>
                  <span class="activity-list__meta">
                    <app-status-chip [status]="item.status" />
                    @if (item.dfInterest) {
                      <!-- The enquiry asked for Debtors Finance too — an interest
                           flag Ops reviews with it, not a second application. -->
                      <app-status-chip
                        class="activity-list__chip--minor"
                        status="DF_INTEREST"
                        [attr.data-testid]="'activity-df-interest-' + item.short_id"
                      />
                    }
                    @if (item.isDraft) {
                      <span class="activity-list__date">Started {{ item.created_at | ago }}</span>
                      <span
                        class="activity-list__continue"
                        aria-label="Draft — continue this application"
                        >Continue &rarr;</span
                      >
                    } @else {
                      <span class="activity-list__date">{{
                        item.created_at | date: 'mediumDate'
                      }}</span>
                    }
                  </span>
                </a>
                <!--
                  Constitution §A2.3 — a client who elected Kickstart only has
                  DEFERRED Debtors Finance, not been declined for it, and the
                  deferral is theirs to reverse: "No Ops action is required to
                  unlock it." Offered on the enquiry row, where the Debtors
                  Finance interest is already shown. Records the intent; it
                  opens no application.
                -->
                @if (item.productLabel === 'Funding enquiry' && canReElectDf()) {
                  <div class="activity-list__reelect" data-testid="df-re-election">
                    <p class="activity-list__reelect-text">
                      Didn't ask about Debtors Finance? You can still be considered — cash advanced
                      against your unpaid invoices.
                      @if (dfReElection()?.stale_bank_data) {
                        <span class="activity-list__reelect-note">
                          We'll ask for up-to-date bank statements before it goes ahead.
                        </span>
                      }
                    </p>
                    <button
                      mat-stroked-button
                      type="button"
                      (click)="reElectDebtorsFinance()"
                      [disabled]="isReElecting()"
                      data-testid="df-re-election-btn"
                    >
                      {{ isReElecting() ? 'Adding…' : 'Add Debtors Finance' }}
                    </button>
                  </div>
                }
              </li>
            }
          </ul>
        }
      </mat-card-content>
    </mat-card>
  }
</div>
